<template>
  <div>
    <header class="site-header">
      <div class="site-header-inner">

        <!-- Left: Logo → homepage -->
        <div class="header-logo" @click="navigateToDashboard">
          <img src="./assets/images/header/logo.png" class="logo-icon" />
          <img src="./assets/images/header/logo_text.png" class="logo-text" />
        </div>

        <!-- Mobile: username + points beside logo -->
        <div class="header-mobile-info" v-if="isLoggedIn">
          <span class="hmi-username">{{ $root.userInfo.username || $root.username }}</span>
          <span class="hmi-points">
            <span class="hmi-crown">👑</span>
            <span class="hmi-val">{{ +parseFloat($root.userInfo.point1 || 0).toFixed(2) }}</span>
          </span>
        </div>

        <!-- Desktop nav links -->
        <nav class="header-desktop-nav" v-if="showDesktopNav">
          <router-link to="/web/homepage" class="dn-link" :class="{ 'dn-active': isActive('homepage') }"><i class="fa fa-home"></i> Home</router-link>
          <router-link to="/web/rumble"   class="dn-link" :class="{ 'dn-active': isActive('rumble') }"><i class="fa fa-archive"></i> Order</router-link>
          <router-link to="/web/product"  class="dn-link" :class="{ 'dn-active': isActive('product') }"><i class="fa fa-shopping-bag"></i> Mall</router-link>
          <router-link to="/web/redeem"   class="dn-link" :class="{ 'dn-active': isActive('redeem') }"><i class="fa fa-exchange"></i> Exchange</router-link>
        </nav>

        <!-- Right -->
        <div class="header-right">

          <!-- Logged in: user info → notification → cart → avatar -->
          <template v-if="isLoggedIn">
            <div class="header-user-info">
              <span class="header-username">{{ $root.userInfo.username || $root.username }}</span>
              <span class="header-points">
                <span class="points-crown">👑</span>
                <span class="points-value">{{ +parseFloat($root.userInfo.point1 || 0).toFixed(2) }}</span>
              </span>
            </div>

            <!-- Notification -->
            <div class="header-icon-btn" @click="$router.push('/web/notifications')">
              <i class="fa fa-bell"></i>
              <span class="hib-badge" v-if="notifCount > 0">{{ notifCount > 99 ? '99+' : notifCount }}</span>
            </div>

            <!-- Cart -->
            <div class="header-icon-btn" @click="$router.push('/web/cart')">
              <i class="fa fa-shopping-cart"></i>
              <span class="hib-badge" v-if="cartCount > 0">{{ cartCount > 99 ? '99+' : cartCount }}</span>
            </div>

            <!-- Avatar (opens sidebar) — just left of language -->
            <div class="header-avatar" @click="avatarClick">
              <img src="./assets/images/header/logo.png" class="avatar-img" />
            </div>
          </template>

          <!-- Not logged in: Login button (visible on homepage2 only) -->
          <button v-else-if="$route.path.includes('homepage2')" class="header-login-btn" @click="$router.push('/web/sessions/login')">
            Login
          </button>

          <!-- Language switcher — always far right -->
          <b-dropdown id="dropdown-lang" variant="link" no-caret right toggle-class="lang-toggle">
            <template #button-content>
              <img :src="flagImg" class="flag-img" />
              <span class="lang-label">{{ langLabel }}</span>
            </template>
            <b-dropdown-item @click="$root.changeLan('en')">
              <img src="./assets/images/flags/en.png" class="flag-img mr-2" /> English
            </b-dropdown-item>
            <b-dropdown-item @click="$root.changeLan('zh')">
              <img src="./assets/images/flags/zh.png" class="flag-img mr-2" /> 中文
            </b-dropdown-item>
            <b-dropdown-item @click="$root.changeLan('my')">
              <img src="./assets/images/flags/my.png" class="flag-img mr-2" /> Malay
            </b-dropdown-item>
          </b-dropdown>

        </div>
      </div>
    </header>

    <!-- Desktop profile sidebar -->
    <b-sidebar id="sidebar-profile" v-model="sidebarOpen" no-header right shadow backdrop width="320px">
      <template #default="{ hide }">
        <div class="ps-wrap">

          <!-- Pink hero banner — ball lives here so it's never clipped -->
          <div class="ps-hero">
            <div class="ps-stars-clip">
              <span v-for="n in 20" :key="n" class="ps-star" :style="psStarStyle(n)"></span>
            </div>
            <button class="ps-close" @click="hide">✕</button>
            <div class="ps-ball-wrap">
              <div class="ps-ball-glow"></div>
              <img :src="rankImg" class="ps-ball-img" />
            </div>
          </div>

          <!-- Scrollable body -->
          <div class="ps-body">

            <!-- Profile card (no ball inside) -->
            <div class="ps-card">
              <div class="ps-tier-badge">{{ rankName }}</div>
              <div class="ps-username">{{ $root.userInfo.username || $root.username }}</div>
              <div class="ps-email" v-if="$root.userInfo.email">{{ $root.userInfo.email }}</div>
              <div class="ps-progress" v-if="nextTierThreshold !== null">
                <div class="ps-progress-header">
                  <span class="ps-progress-title">Total Spend</span>
                  <span class="ps-progress-amount">RM {{ totalSpend }} / RM {{ nextTierThreshold }}</span>
                </div>
                <div class="ps-bar-track">
                  <div class="ps-bar-fill" :style="{ width: spendPct + '%' }"></div>
                </div>
                <div class="ps-progress-hint">
                  Spend RM {{ +(nextTierThreshold - totalSpend).toFixed(2) }} more to upgrade {{ nextTierName }}
                  <span class="ps-pct">{{ spendPct.toFixed(0) }}%</span>
                </div>
              </div>
              <div class="ps-max-badge" v-else>👑 You are our King</div>
            </div>

            <!-- My Activity -->
            <div class="ps-section-label">My Activity</div>
            <div class="ps-menu-card">
              <router-link to="/web/rumble" class="ps-item" @click.native="hide">
                <div class="ps-icon-wrap ps-icon-orange"><i class="fa fa-archive"></i></div>
                <span class="ps-item-label">Inventory</span>
                <i class="fa fa-chevron-right ps-item-arrow"></i>
              </router-link>
            </div>

            <!-- Account Settings -->
            <div class="ps-section-label">Account Settings</div>
            <div class="ps-menu-card">
              <router-link to="/web/profile/info" class="ps-item" @click.native="hide">
                <div class="ps-icon-wrap ps-icon-blue"><i class="fa fa-user"></i></div>
                <span class="ps-item-label">Profile Information</span>
                <i class="fa fa-chevron-right ps-item-arrow"></i>
              </router-link>
              <div class="ps-mi-divider"></div>
              <router-link to="/web/profile/security" class="ps-item" @click.native="hide">
                <div class="ps-icon-wrap ps-icon-purple"><i class="fa fa-lock"></i></div>
                <span class="ps-item-label">Account Security</span>
                <i class="fa fa-chevron-right ps-item-arrow"></i>
              </router-link>
              <div class="ps-mi-divider"></div>
              <router-link to="/web/profile/kyc" class="ps-item" @click.native="hide">
                <div class="ps-icon-wrap ps-icon-green"><i class="fa fa-id-card"></i></div>
                <span class="ps-item-label">KYC Verification</span>
                <i class="fa fa-chevron-right ps-item-arrow"></i>
              </router-link>
            </div>

            <!-- Logout -->
            <div class="ps-logout-wrap">
              <button class="ps-logout-btn" @click="logout">
                <i class="fa fa-sign-out"></i> Logout
              </button>
            </div>
            <div class="ps-copy">© 2026 Slowpoke Corner. All rights reserved.</div>

          </div>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
export default {
  data() {
    return {
      sidebarOpen: false,
      notifCount: 0,
      cartCount: 0,
    };
  },
  mounted() {
    if (this.isLoggedIn && !this.$root.userInfo.username) {
      this.$root.getInfo();
    }
  },
  computed: {
    isLoggedIn() {
      return this.$root.loggedIn;
    },
    tier() {
      return parseInt(this.$root.userInfo.membership_tier || 1);
    },
    rankName() {
      const map = { 1: 'Poké Ball', 2: 'Great Ball', 3: 'Ultra Ball', 4: 'Master Ball' };
      return map[this.tier] || 'Poké Ball';
    },
    rankImg() {
      const map = {
        1: require('./assets/images/rank/pokeball.png'),
        2: require('./assets/images/rank/greatball.png'),
        3: require('./assets/images/rank/ultraball.png'),
        4: require('./assets/images/rank/masterball.png'),
      };
      return map[this.tier] || map[1];
    },
    totalSpend() {
      return parseFloat(this.$root.userInfo.total_spend || 0).toFixed(2);
    },
    nextTierThreshold() {
      const next = { 1: 500, 2: 5000, 3: 10000, 4: null };
      return next[this.tier] ?? null;
    },
    nextTierName() {
      const map = { 1: 'Great Ball', 2: 'Ultra Ball', 3: 'Master Ball', 4: null };
      return map[this.tier] || null;
    },
    spendPct() {
      const t = this.nextTierThreshold;
      if (!t) return 100;
      return Math.min(100, (parseFloat(this.totalSpend) / t) * 100);
    },
    flagImg() {
      const map = {
        zh: require('./assets/images/flags/zh.png'),
        my: require('./assets/images/flags/my.png'),
      };
      return map[this.$i18n.locale] || require('./assets/images/flags/en.png');
    },
    langLabel() {
      const map = { zh: '中文', my: 'MY', en: 'EN' };
      return map[this.$i18n.locale] || 'EN';
    },
    showDesktopNav() {
      return this.isLoggedIn &&
        !this.$route.path.includes('sessions') &&
        !this.$route.path.includes('register');
    },
  },
  methods: {
    isActive(name) {
      return this.$route.path.includes('/' + name);
    },
    psStarStyle(n) {
      const size = (n % 3 === 0) ? '3px' : (n % 2 === 0) ? '2px' : '1.5px';
      return {
        width: size, height: size,
        top: ((n * 37 + n * 13) % 100) + '%',
        left: ((n * 53 + n * 7) % 100) + '%',
        animationDelay: (n * 0.3) % 3 + 's',
      };
    },
    avatarClick() {
      if (!this.isLoggedIn) {
        this.$router.push('/web/sessions/login');
        return;
      }
      if (window.innerWidth <= 768) {
        this.$router.push('/web/profile');
      } else {
        this.sidebarOpen = !this.sidebarOpen;
      }
    },
    logout() {
      localStorage.removeItem('currencyToken');
      localStorage.removeItem('info');
      this.$root.loggedIn = false;
      this.$root.username = '';
      this.sidebarOpen = false;
      this.$router.push('/web/homepage');
    },
    navigateToDashboard() {
      if (this.$route.path === '/web/homepage') {
        this.$router.go(0);
      } else {
        this.$router.push('/web/homepage');
      }
    },
  },
};
</script>

<style>
/* ── Site header ── */
.site-header {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 100;
  background: linear-gradient(120deg, #ffc0cb, #ff99cc, #ffb6c1, #ff7f7f);
  box-shadow: 0 2px 16px rgba(255,100,130,0.35);
  border-bottom: 1px solid rgba(255,255,255,0.25);
}

.site-header-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  height: 62px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Logo */
.header-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  flex-shrink: 0;
}
.logo-icon {
  height: 38px;
  width: auto;
  filter: drop-shadow(0 1px 4px rgba(0,0,0,0.2));
}
.logo-text {
  height: 26px;
  width: auto;
  max-width: 200px;
}

/* Mobile: hide text logo, keep icon only */
@media (max-width: 480px) {
  .logo-text { display: none; }
  .site-header-inner { padding: 0 14px; }
}

/* Login button */
.header-login-btn {
  background: rgba(255,255,255,0.25);
  border: 1.5px solid rgba(255,255,255,0.6);
  border-radius: 999px;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 6px 18px;
  cursor: pointer;
  transition: background 0.15s;
  letter-spacing: 0.3px;
}
.header-login-btn:hover {
  background: rgba(255,255,255,0.38);
}

/* Mobile: hide desktop-only elements */
@media (max-width: 768px) {
  .header-avatar    { display: none !important; }
  .header-user-info { display: none !important; }
}

/* Mobile user info beside logo — hidden on desktop */
.header-mobile-info { display: none; }
@media (max-width: 768px) {
  .header-mobile-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 3px;
    min-width: 0;
    flex: 1;
    padding-left: 8px;
  }
  .hmi-username {
    font-size: 0.75rem;
    font-weight: 800;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
    line-height: 1;
  }
  .hmi-points {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: rgba(255,255,255,0.25);
    border-radius: 999px;
    padding: 2px 8px 2px 5px;
  }
  .hmi-crown { font-size: 0.65rem; line-height: 1; }
  .hmi-val {
    font-size: 0.65rem;
    font-weight: 700;
    color: #fff;
  }
}

/* Notification / Cart icon buttons */
.header-icon-btn {
  position: relative;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255,255,255,0.22);
  border: 1px solid rgba(255,255,255,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  color: #fff;
  font-size: 0.95rem;
}
.header-icon-btn:hover { background: rgba(255,255,255,0.35); }
@media (max-width: 768px) {
  .header-icon-btn { width: 32px; height: 32px; font-size: 0.82rem; }
}
.hib-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #ff3b30;
  color: #fff;
  font-size: 0.55rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #fff;
  line-height: 1;
}

/* Right section */
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
@media (max-width: 768px) {
  .header-right { gap: 6px; }
}

/* Language toggle button */
.lang-toggle {
  display: flex !important;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.2) !important;
  border: 1px solid rgba(255,255,255,0.4) !important;
  border-radius: 999px !important;
  padding: 5px 12px 5px 8px !important;
  transition: background 0.15s;
  color: #fff !important;
  text-decoration: none !important;
}
.lang-toggle:hover {
  background: rgba(255,255,255,0.32) !important;
}
/* Mobile: smaller toggle, no arrow */
@media (max-width: 768px) {
  .lang-toggle {
    padding: 4px 8px !important;
    gap: 4px;
  }
  .lang-caret { display: none !important; }
  .lang-label { font-size: 0.7rem !important; }
}
.flag-img {
  width: 20px;
  height: 14px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  display: inline-block;
  vertical-align: middle;
}
.lang-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
}
.lang-caret {
  font-size: 0.6rem;
  color: rgba(255,255,255,0.7);
  margin-left: 2px;
}

/* Dropdown item flags */
.dropdown-item .flag-img {
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

/* Slowpoke avatar */
.header-avatar {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255,255,255,0.25);
  border: 2px solid rgba(255,255,255,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: visible;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 10px rgba(255,100,130,0.3);
}
.header-avatar:hover {
  background: rgba(255,255,255,0.38);
  border-color: #fff;
  transform: scale(1.06);
  box-shadow: 0 4px 16px rgba(255,100,130,0.45);
}
.avatar-img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.avatar-login-dot {
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #22c55e;
  border: 1.5px solid #fff;
  box-shadow: 0 0 6px rgba(34,197,94,0.7);
}

/* ── Desktop nav ── */
.header-desktop-nav {
  display: none;
}
@media (min-width: 769px) {
  .header-desktop-nav {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    justify-content: center;
  }
  .dn-link {
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 0.82rem;
    font-weight: 600;
    color: rgba(255,255,255,0.85);
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
    letter-spacing: 0.3px;
  }
  .dn-link:hover {
    background: rgba(255,255,255,0.18);
    color: #fff;
  }
  .dn-link.dn-active {
    background: rgba(255,255,255,0.25);
    color: #fff;
  }
}

/* ── Profile sidebar ── */
.ps-wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f2f2f7;
  overflow: hidden;
}

/* Hero — overflow visible so ball can extend below */
.ps-hero {
  position: relative;
  height: 96px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #ff6b8a 0%, #ff8fab 40%, #ffb3c8 70%, #ff99cc 100%);
  overflow: visible;
  z-index: 1;
}
/* Stars clipped inside hero bounds */
.ps-stars-clip {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: 0;
  pointer-events: none;
}
.ps-star {
  position: absolute;
  border-radius: 50%;
  background: rgba(255,255,255,0.7);
  animation: tw 2.5s ease-in-out infinite alternate;
}
.ps-close {
  position: absolute;
  top: 12px; right: 14px;
  background: rgba(0,0,0,0.15);
  border: none;
  color: #fff;
  font-size: 0.9rem;
  width: 28px; height: 28px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  z-index: 3;
}
.ps-close:hover { background: rgba(0,0,0,0.28); }

/* Ball anchored to bottom of hero, straddles hero/card */
.ps-ball-wrap {
  position: absolute;
  bottom: -34px;
  left: 50%;
  transform: translateX(-50%);
  width: 68px; height: 68px;
  z-index: 2;
  display: flex; align-items: center; justify-content: center;
}
.ps-ball-glow {
  position: absolute; inset: 0; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,107,138,0.35) 0%, transparent 70%);
  filter: blur(5px);
}
.ps-ball-img {
  width: 60px; height: 60px;
  object-fit: contain; position: relative; z-index: 1;
  filter: drop-shadow(0 3px 10px rgba(255,107,138,0.4));
}

/* Scrollable body — top padding clears the overlapping ball */
.ps-body {
  flex: 1;
  overflow-y: auto;
  padding: 44px 14px 28px;
  display: flex;
  flex-direction: column;
}

/* Profile card */
.ps-card {
  background: #fff;
  border-radius: 18px;
  padding: 14px 16px 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.10);
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 14px;
}
.ps-username {
  font-size: 0.95rem; font-weight: 800; color: #1a1a2e; margin-bottom: 2px;
}
.ps-email {
  font-size: 0.72rem; color: #999; margin-bottom: 10px;
}
.ps-badges {
  display: flex; gap: 6px; margin-bottom: 12px; flex-wrap: wrap; justify-content: center;
}
.ps-tier-badge {
  background: linear-gradient(120deg, #ff6b8a, #ff99cc);
  color: #fff; font-size: 0.68rem; font-weight: 700;
  padding: 3px 10px; border-radius: 999px;
  box-shadow: 0 2px 6px rgba(255,107,138,0.35);
}
.ps-points-badge {
  background: #fff8e1; border: 1.5px solid #ffd54f;
  color: #b8860b; font-size: 0.68rem; font-weight: 700;
  padding: 3px 10px; border-radius: 999px;
}
.ps-progress { width: 100%; }
.ps-progress-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;
}
.ps-progress-title {
  font-size: 0.72rem; font-weight: 700; color: #555;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.ps-progress-amount { font-size: 0.72rem; font-weight: 600; color: #333; }
.ps-bar-track {
  width: 100%; height: 8px; background: #f0e6ea; border-radius: 999px; overflow: hidden;
}
.ps-bar-fill {
  height: 100%; border-radius: 999px;
  background: linear-gradient(90deg, #ff6b8a, #ff99cc);
  transition: width 0.8s ease; min-width: 5px;
  box-shadow: 0 0 5px rgba(255,107,138,0.5);
}
.ps-progress-hint {
  display: flex; justify-content: space-between;
  margin-top: 5px; font-size: 0.65rem; color: #bbb;
}
.ps-pct { font-weight: 700; color: #ff6b8a; }
.ps-max-badge {
  font-size: 0.78rem; font-weight: 700; color: #b8860b;
  background: #fff8e1; border: 1.5px solid #ffd54f;
  padding: 5px 14px; border-radius: 999px;
}

/* Menu */
.ps-section-label {
  font-size: 0.65rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.8px; color: #aaa; margin-bottom: 7px; padding-left: 4px;
}
.ps-menu-card {
  background: #fff; border-radius: 14px; overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06); margin-bottom: 20px;
}
.ps-item {
  display: flex; align-items: center; padding: 12px 14px;
  gap: 11px; color: #1a1a2e; text-decoration: none; transition: background 0.15s;
}
.ps-item:hover, .ps-item:active {
  background: #fafafa; text-decoration: none; color: #1a1a2e;
}
.ps-icon-wrap {
  width: 32px; height: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.8rem; color: #fff; flex-shrink: 0;
}
.ps-icon-blue   { background: linear-gradient(135deg, #4f9ef8, #72b8ff); box-shadow: 0 2px 6px rgba(79,158,248,0.35); }
.ps-icon-purple { background: linear-gradient(135deg, #bf5af2, #d97aff); box-shadow: 0 2px 6px rgba(191,90,242,0.35); }
.ps-icon-green  { background: linear-gradient(135deg, #30d158, #5de083); box-shadow: 0 2px 6px rgba(48,209,88,0.35); }
.ps-icon-orange { background: linear-gradient(135deg, #ff9500, #ffb340); box-shadow: 0 2px 6px rgba(255,149,0,0.35); }
.ps-item-label { flex: 1; font-size: 0.86rem; font-weight: 500; }
.ps-item-arrow { font-size: 0.62rem; color: #c8c8ce; }
.ps-mi-divider { height: 1px; background: #f2f2f7; margin-left: 57px; }

/* Logout — pinned to bottom when content is short */
.ps-logout-wrap { margin-top: auto; padding-top: 16px; margin-bottom: 12px; }
.ps-logout-btn {
  width: 100%; padding: 13px; border: none; border-radius: 12px;
  background: linear-gradient(120deg, #ff6b8a, #ff99cc);
  color: #fff; font-size: 0.9rem; font-weight: 700;
  cursor: pointer; letter-spacing: 0.3px;
  box-shadow: 0 4px 16px rgba(255,107,138,0.4);
  transition: opacity 0.2s, transform 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 7px;
}
.ps-logout-btn:hover  { opacity: 0.9; transform: translateY(-1px); }
.ps-logout-btn:active { opacity: 1; transform: translateY(0); }
.ps-copy {
  font-size: 0.62rem; color: #bbb;
  text-align: center;
}

/* Override topBar-home so it doesn't conflict */
.topBar-home { display: none !important; }
.header_wrap { display: none !important; }

/* Make #app clear the fixed header */
#app { margin-top: 62px !important; }

/* User info (username + points) */
.header-user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}
.header-username {
  font-size: 0.78rem;
  font-weight: 700;
  color: #fff;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.header-points {
  display: flex;
  align-items: center;
  gap: 3px;
  background: rgba(255,255,255,0.2);
  border-radius: 999px;
  padding: 1px 8px 1px 5px;
  margin-top: 2px;
}
.points-crown {
  font-size: 0.75rem;
  line-height: 1;
}
.points-value {
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.3px;
}

@media (max-width: 360px) {
  .header-username { max-width: 60px; }
}
</style>
